import React, { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon, UserGroupIcon, PlusIcon } from '@heroicons/react/20/solid';
import { useTeamsContext } from './ContextProviders/TeamsContextProvider';
import { useMyContext } from './ContextProviders/CreditsContextProvider';
import { switchTeam } from '../Requests/User/UserRequests';
import { useTranslation } from 'react-i18next';
import { clearTeamSpecificCaches } from '../utils/teamCacheManager';
import CreateTeamModal from './CreateTeamModal';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function TeamSwitcher() {
  const { teams, currentTeam, switchToTeam } = useTeamsContext();
  const { setCredits } = useMyContext();
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { t } = useTranslation();


  const handleTeamSwitch = async (teamUuid) => {
    if (teamUuid === currentTeam?.uuid || isLoading) {
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await switchTeam(teamUuid);
      
      if (response.data && response.data.team) {
        // Update local team state
        const newTeam = switchToTeam(teamUuid);
        
        if (newTeam) {
          // Update credits context
          setCredits(newTeam.credits);
          
          // Clear team-specific cached data
          clearTeamSpecificCaches();
          
          // Refresh page to load new team data
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Failed to switch teams:', error);
      
      if (error.response?.status === 403) {
        alert('You do not have access to that team');
      } else {
        alert('Failed to switch teams. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTeamClick = () => {
    setShowCreateModal(true);
  };

  // Don't render if no teams at all
  if (!teams || teams.length === 0) {
    return null;
  }

  return (
    <div className="px-4 mb-2">
      <Menu as="div" className="relative inline-block text-left w-full">
          <div>
            <Menu.Button className="inline-flex w-full justify-between items-center rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black">
            <div className="flex items-center min-w-0 flex-1">
              <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {currentTeam?.name || 'Select Team'}
                </p>
              </div>
            </div>
            <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-2 flex-shrink-0" aria-hidden="true" />
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute left-0 right-0 z-10 mt-2 origin-top-left divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              {teams.map((team) => (
                <Menu.Item key={team.uuid}>
                  {({ active }) => (
                    <button
                      onClick={() => handleTeamSwitch(team.uuid)}
                      disabled={isLoading}
                      className={classNames(
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        'group flex w-full items-center justify-between px-4 py-2 text-sm',
                        isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      )}
                    >
                      <div className="flex items-center min-w-0">
                        <UserGroupIcon className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0" />
                        <span className="truncate">{team.name}</span>
                      </div>
                      {currentTeam?.uuid === team.uuid && (
                        <CheckIcon className="h-4 w-4 text-green-600 ml-2 flex-shrink-0" />
                      )}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
            
            {/* Create New Team Section */}
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleCreateTeamClick}
                    className={classNames(
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                      'group flex w-full items-center px-4 py-2 text-sm cursor-pointer'
                    )}
                  >
                    <PlusIcon className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0" />
                    Create New Team
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      {/* Create Team Modal */}
      {showCreateModal && (
        <CreateTeamModal
          handleClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
}